export function BarChart({ data, color1, color2, yLabel, maxY: maxYProp, highlightIndex, height, onBarClick, barGap, axisFontSize = 8 }) {
  const maxY = maxYProp ?? Math.max(...data.map(d => d.value ?? 0), 1);
  const W = 400, H = height ?? 200, padL = 30, padB = 30, padT = 14, padR = 10;
  const iW = W - padL - padR, iH = H - padT - padB;
  const barW = Math.min(40, Math.max(6, iW / data.length - (barGap ?? 4)));
  // color1 may be a CSS var (the Default background passes the accent), so keep
  // only id-safe characters.
  const idPart = String(color1).replace(/[^a-zA-Z0-9-]/g, "");
  const gradId = `grad-${idPart}`;
  const hlGradId = `grad-hl-${idPart}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: color1 }} />
          <stop offset="100%" style={{ stopColor: color2 }} />
        </linearGradient>
        <linearGradient id={hlGradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--c-primary)" />
          <stop offset="100%" stopColor="var(--c-primary-deep)" />
        </linearGradient>
      </defs>
      {[0,0.25,0.5,0.75,1].map(f => {
        const y = padT + iH * (1 - f);
        const label = maxYProp ? `${Math.round(maxY*f)}%` : Math.round(maxY*f);
        return <g key={f}><line x1={padL} y1={y} x2={W-padR} y2={y} stroke="var(--c-grid-line-soft)" strokeWidth="1"/><text x={padL-4} y={y+4} fontSize={axisFontSize} fontWeight="700" fill="var(--c-text-soft)" textAnchor="end">{label}</text></g>;
      })}
      {data.map((d, i) => {
        const x = padL + i * (iW / data.length) + (iW / data.length - barW) / 2;
        const isHL = highlightIndex === i;
        // Bug fix: null means no habits were due that day — render label but skip bar
        if (d.value === null) {
          return (
            <g key={i}>
              {isHL && <rect x={x-3} y={padT} width={barW+6} height={iH} fill="var(--c-bg-active)" rx={4} opacity="0.5"/>}
              {data.length <= 12 && <text x={x + barW/2} y={H-4} fontSize={axisFontSize} fill={isHL ? "var(--c-text-accent)" : "var(--c-text-soft)"} fontWeight="700" textAnchor="middle">{d.label}</text>}
              {data.length > 12 && data.length <= 31 && (() => {
                const step = Math.floor((data.length - 1) / 4);
                const show = [0, step, step*2, step*3, data.length - 1];
                return show.includes(i) ? <text x={x + barW/2} y={H-4} fontSize={axisFontSize} fontWeight="700" fill={isHL ? "var(--c-text-accent)" : "var(--c-text-soft)"} textAnchor="middle">{d.label}</text> : null;
              })()}
            </g>
          );
        }
        const barH = (d.value / maxY) * iH;
        const y = padT + iH - barH;
        return (
          <g key={i}>
            {isHL && <rect x={x-3} y={padT} width={barW+6} height={iH} fill="var(--c-bg-active)" rx={4} opacity="0.5"/>}
            <rect x={x} y={y} width={barW} height={Math.max(barH, 1)} fill={isHL ? `url(#${hlGradId})` : `url(#${gradId})`} rx={3}>
              <title>{d.label}: {d.value}{yLabel}</title>
            </rect>
            {isHL && barH > 0 && <text x={x + barW/2} y={y - 4} fontSize={axisFontSize} fill="var(--c-text-accent)" fontWeight="700" textAnchor="middle">{d.value}%</text>}
            {data.length <= 12 && <text x={x + barW/2} y={H-4} fontSize={axisFontSize} fill={isHL ? "var(--c-text-accent)" : "var(--c-text-soft)"} fontWeight="700" textAnchor="middle">{d.label}</text>}
            {data.length > 12 && data.length <= 31 && (() => {
              // Show 5 evenly spaced labels across the data range
              const step = Math.floor((data.length - 1) / 4);
              const show = [0, step, step*2, step*3, data.length - 1];
              return show.includes(i) ? <text x={x + barW/2} y={H-4} fontSize={axisFontSize} fontWeight="700" fill={isHL ? "var(--c-text-accent)" : "var(--c-text-soft)"} textAnchor="middle">{d.label}</text> : null;
            })()}
            {data.length > 31 && i % 5 === 0 && <text x={x + barW/2} y={H-4} fontSize={Math.max(axisFontSize - 1, 6)} fontWeight="700" fill="var(--c-text-soft)" textAnchor="middle">{d.label}</text>}
          </g>
        );
      })}
      {/* Transparent full-height click targets per column (top layer) */}
      {onBarClick && data.map((d, i) => (
        <rect key={`hit-${i}`} x={padL + i * (iW / data.length)} y={padT} width={iW / data.length} height={iH}
          fill="transparent" style={{ cursor: "pointer" }} onClick={() => onBarClick(i)} />
      ))}
    </svg>
  );
}
